/**
 * Spelling Regels (SPR) — 20 families, 5 skills
 *
 * Skills: klankgroepen (6), weetwoorden (3), leenwoorden (5),
 *         samenstelling (2), leestekens (4)
 *
 * Split from taxonomyRegistry.ts in R23.
 *
 * LDG Audit (16 mrt 2026): 3 tier-aanpassingen doorgevoerd:
 * - SPR-ACCENT: E8+ (tier 16) → B8 (tier 13) — standaard groep 8 spelling, niet VO-voorbereiding
 * - SPR-SAM-TUSSEN-N: E7 (tier 11) → M7 (tier 9) — Staal behandelt dit midden groep 7
 * - SPR-SAM-TUSSEN-S: B8 (tier 13) → E7 (tier 11) — moeilijker dan tussen-n maar nog groep 7
 */

import type { FamilyDefinition } from '../types'

export const SPR_FAMILIES: FamilyDefinition[] = [
  // 5.1 Skill: klankgroepen (6 families)
  {
    familyId: 'SPR-KLANK-JAGER',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_open_lange',
    tier: 1,
    levelCode: 'B6',
    description: 'Open lettergreep (lange klank)',
  },
  {
    familyId: 'SPR-KLANK-BAKKER',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_gesloten_korte',
    tier: 1,
    levelCode: 'B6',
    description: 'Gesloten lettergreep (korte klank)',
  },
  {
    familyId: 'SPR-KLANK-VERDUBBEL',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_medeklinker_verdubbel',
    tier: 3,
    levelCode: 'M6',
    description: 'Medeklinker verdubbelen',
  },
  {
    familyId: 'SPR-KLANK-VERKORTEN',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_klinker_verkorten',
    tier: 3,
    levelCode: 'M6',
    description: 'Klinker verkorten',
  },
  {
    familyId: 'SPR-KLANK-AA-EE-OO',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_lange_klinkers',
    tier: 2,
    levelCode: 'B6+',
    description: 'Lange klinkers (aa/ee/oo)',
  },
  {
    familyId: 'SPR-KLANK-AU-OU',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_au_ou',
    tier: 5,
    levelCode: 'E6',
    description: 'Au/ou onderscheid',
  },
  // 5.2 Skill: weetwoorden (3 families)
  {
    familyId: 'SPR-WEET-EI-IJ',
    module: 'SpellingRegel',
    skill: 'weetwoorden',
    subSkill: 'weet_ei_ij',
    tier: 2,
    levelCode: 'B6+',
    description: 'Ei/ij weetwoorden',
  },
  {
    familyId: 'SPR-WEET-D-T',
    module: 'SpellingRegel',
    skill: 'weetwoorden',
    subSkill: 'weet_d_t_eind',
    tier: 3,
    levelCode: 'M6',
    description: 'D/t aan eind woord',
  },
  {
    familyId: 'SPR-WEET-DT',
    module: 'SpellingRegel',
    skill: 'weetwoorden',
    subSkill: 'weet_dt_woorden',
    tier: 5,
    levelCode: 'E6',
    description: 'Dt-woorden (wordt/vindt)',
  },
  // 5.3 Skill: leenwoorden (5 families)
  {
    familyId: 'SPR-LEEN-C-S-K',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_c_uitspraak',
    tier: 7,
    levelCode: 'B7',
    description: 'C als S of K',
  },
  {
    familyId: 'SPR-LEEN-FRANS',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_frans',
    tier: 10,
    levelCode: 'M7+',
    description: 'Franse leenwoorden (-eau/-oir)',
  },
  {
    familyId: 'SPR-LEEN-ENGELS',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_engels',
    tier: 13,
    levelCode: 'B8',
    description: 'Engelse leenwoorden',
  },
  {
    familyId: 'SPR-LEEN-LATIJN',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_latijn_mv',
    tier: 15,
    levelCode: 'E8',
    description: 'Klassieke meervouden',
  },
  {
    familyId: 'SPR-LEEN-GRIEKS',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_grieks',
    tier: 16,
    levelCode: 'E8+',
    description: 'Griekse leenwoorden',
  },
  // 5.4 Skill: samenstelling (2 families)
  {
    familyId: 'SPR-SAM-TUSSEN-N',
    module: 'SpellingRegel',
    skill: 'samenstelling',
    subSkill: 'samenst_tussen_n',
    tier: 9,
    levelCode: 'M7',
    description: 'Tussen-n regel (pannenkoek, zonnebloem)',
  },
  {
    familyId: 'SPR-SAM-TUSSEN-S',
    module: 'SpellingRegel',
    skill: 'samenstelling',
    subSkill: 'samenst_tussen_s',
    tier: 11,
    levelCode: 'E7',
    description: 'Tussen-s regel (dorpsplein, koningshuis)',
  },
  // 5.5 Skill: leestekens (4 families)
  {
    familyId: 'SPR-HOOFDLETTER',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'hoofdletter_gebruik',
    tier: 1,
    levelCode: 'B6',
    description: 'Hoofdlettergebruik (basis essentieel)',
  },
  {
    familyId: 'SPR-LEESTEKEN-PUNT-KOMMA',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'leesteken_punt_komma',
    tier: 5,
    levelCode: 'E6',
    description: 'Punt en komma gebruik (basis)',
  },
  {
    familyId: 'SPR-TREMA-MV',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'trema_meervoud',
    tier: 9,
    levelCode: 'M7',
    description: 'Trema bij meervoud',
  },
  {
    familyId: 'SPR-ACCENT',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'accent_klemtoon',
    tier: 13,
    levelCode: 'B8',
    description: 'Accent aigu (vóór/één) — CITO reguliere spellingkennis',
  },
]
